export class ModelUtils {
    //-------------------------------------------------------------------------
    /**
     * Nastaveni opacity pro jednotlive meshe modelu (0 az 1). Material se nemeni, opacity se nastavuje primo jednotlivym meshInstaces v modelu jako parametr "material_opacity".
     * Aby to fungovalo, tak materialy meshy musi mit spravne nastaveny blendMode, ktery podporuje alfa kanal.
     * @param model Model
     * @param opacity 0 az 1
     */
    //-------------------------------------------------------------------------
    static setModelMeshesOpacity(model: pc.ModelComponent, opacity: number): void {
        model.meshInstances.forEach((meshIns: pc.MeshInstance) => {
            meshIns.setParameter("material_opacity", opacity);
        });
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static showModel(model: pc.ModelComponent): void {
        let meshInstances = model.meshInstances;
        let i = meshInstances.length;

        while (i-- != 0)
            meshInstances[i].visible = true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static hideMode(model: pc.ModelComponent): void {
        let meshInstances = model.meshInstances;
        let i = meshInstances.length;

        while (i-- != 0)
            meshInstances[i].visible = false;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci index meshInstance v modelu podle jmena node nebo -1, pokud zadne meshInstance nema node s danym jmenem
     */
    //-------------------------------------------------------------------------
    static getMeshInstanceId(model: pc.ModelComponent, nodeName: string): number {
        let instances = model.meshInstances;

        for (let i = instances.length - 1; i >= 0; i--) {
            if (instances[i].node.name == nodeName)
                return i;
        }

        return -1;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pc.MeshInstance v modelu podle jmena node nebo undefined.
     * @param model
     * @param nodeName 
     */
    //-------------------------------------------------------------------------
    static getMeshInstance(model: pc.ModelComponent, nodeName: string): pc.MeshInstance {
        let instances = model.meshInstances;

        for (let i = instances.length - 1; i >= 0; i--) {
            if (instances[i].node.name = nodeName)
                return instances[i];
        }

        return undefined;
    }

    //-------------------------------------------------------------------------    
    //-------------------------------------------------------------------------
    static cloneModelMaterials(model: pc.ModelComponent, data: object, originalMats: pc.StandardMaterial[], clonedMats: pc.StandardMaterial[], assets?: pc.AssetRegistry, assetPrefix?: string, replace: boolean = false): void {
        model.meshInstances.forEach((mesh: pc.MeshInstance, meshId: number) => {
            let mat = mesh.material as pc.StandardMaterial;
            let matId = originalMats.findIndex((m: pc.StandardMaterial): boolean => { return m == mat; });

            originalMats.push(mat);

            if (matId >= 0) {
                clonedMats.push(clonedMats[matId]);
            }
            else {
                clonedMats.push(ModelUtils.cloneMaterial(mat, data, assets, assetPrefix, replace));
            }
        });
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    static cloneMaterial(material: pc.StandardMaterial, data: object, assets?: pc.AssetRegistry, assetPrefix?: string, replace: boolean = false): pc.StandardMaterial {
        // Podivat se zda material z danym prefixem jiz je v asset registru
        if (assets) {
            let asset = assets.find(assetPrefix + material.name, pc.eAssetType.material);

            if (asset) {
                if (!replace)
                    return asset.resource as pc.StandardMaterial;

                (<pc.Material>asset.resource).destroy();
                assets.remove(asset);
            }
        }

        let mat = material.clone();

        Object.keys(data).forEach((key: string) => {
            (<any>mat)[key] = (<any>data)[key];
        });

        mat.update();

        if (assets) {
            let asset = new pc.Asset(assetPrefix + mat.name, pc.eAssetType.material);

            asset.resource = mat;
            assets.add(asset);
        }

        return mat;
    }

    //-------------------------------------------------------------------------
    /**
     * Vypocita celkovy AABB pro zadanou entitu. Pri vypoctu pouzije i dcerinne entity.
     * @param entity 
     * @param res Do teto promenne bude ulozen vysledek
     */
    //-------------------------------------------------------------------------
    static getEntityTotalAABB(entity: pc.Entity, res: pc.BoundingBox): void {
        res.halfExtents.set(0, 0, 0);

        // Total AABB modelu entity
        this.getModelTotalAABB(entity.model, res);

        // Pridat AABB dcerinnych entit
        entity.children.forEach((child: pc.GraphNode) => {
            if (child instanceof pc.Entity)
                this.getModelTotalAABB((<pc.Entity>child).model, res);
        });
    }

    //-------------------------------------------------------------------------
    /**
     * Vypocita celkovy AABB pro zadany model.
     * @param model 
     * @param res 
     */
    //-------------------------------------------------------------------------
    static getModelTotalAABB(model: pc.ModelComponent, res: pc.BoundingBox,): void {
        if (model) {
            let meshCnt = model.meshInstances.length;

            if (meshCnt != 0) {
                let meshId = 0;

                // Inicializace vysledneho bounding boxu
                if (res.halfExtents.x == 0) {
                    let aabb = model.meshInstances[0].aabb;

                    res.center.copy(aabb.center);
                    res.halfExtents.copy(aabb.halfExtents);

                    meshId = 1;
                }

                while (meshId < meshCnt) {
                    res.add(model.meshInstances[meshId++].aabb);
                }
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vytvori pozadovany pocet klonu zakladniho modelu a ulozi je do app.assets. Kazdy klon ma pousunute UV o zadanou hodnotu.
     * @param baseModel 
     * @param cloneCnt 
     * @param cloneUVShiftX 
     * @param cloneUVShiftY 
     * @param repository 
     * @param meshPosBufSize 
     */
    //-------------------------------------------------------------------------
    static createShiftUVsClones(app: pc.Application, baseModel: pc.Model, cloneCnt: number, cloneUVShiftX: number, cloneUVShiftY: number, cloneAssetKetBase: string, cloneRepo: pc.Asset[], meshPosBufSize: number): void {
        let models: pc.Model[] = [];

        baseModel.meshInstances.forEach((baseMeshIns: pc.MeshInstance, baseMeshInsId: number) => {
            let baseMesh = baseMeshIns.mesh;

            // Pozice vrcholu
            let verPositions = new Float32Array(meshPosBufSize);
            let verCnt = baseMesh.getPositions(verPositions);

            // UVs
            let uvs = new Float32Array(verCnt * 2);
            baseMesh.getUvs(0, uvs);

            // Indicies
            let indicies = new Uint32Array(verCnt * 2);
            let indiciesCnt = baseMesh.getIndices(indicies);

            // Normals
            let normals = new Float32Array(verCnt * 3);
            baseMesh.getNormals(normals);

            for (let cloneId = 0; cloneId < cloneCnt; cloneId++) {
                // Vytvorit model
                let model: pc.Model;

                if (baseMeshInsId == 0) {
                    let node = new pc.GraphNode();

                    model = new pc.Model();
                    model.graph = node;
                    models.push(model);
                }
                else {
                    model = models[cloneId];
                }

                // Posun UVs pro klon
                if (cloneId != 0) {
                    for (let i = 0; i < verCnt; i++) {
                        uvs[i << 1] += cloneUVShiftX;
                        uvs[(i << 1) + 1] += cloneUVShiftY;
                    }
                }

                // Vytvorit mesh
                let mesh = new pc.Mesh(app.graphicsDevice);

                mesh.setPositions(verPositions, 3, verCnt);
                mesh.setUvs(0, uvs, 2, verCnt);
                mesh.setIndices(indicies, indiciesCnt);
                mesh.setNormals(normals, 3, verCnt);
                mesh.update();

                // Vytvorit mesh instancy a pridat ji do modelu
                model.meshInstances.push(new pc.MeshInstance(mesh, baseMeshIns.material, model.graph));
            }
        });

        // Z modelu vytvorit assety a ulozit je do assets
        models.forEach((model: pc.Model, i: number) => {
            let asset = new pc.Asset(cloneAssetKetBase + "_" + i, pc.ASSET_MODEL);

            asset.resource = model;
            app.assets.add(asset);
            cloneRepo.push(asset);
        });
    }

    //-------------------------------------------------------------------------
    /**
     * Vytvoreni modelu se zadanymi meshy
     * @param meshes Pole mesh
     * @param meshMaterials Materialy pro jednotlive mesh
     */
    //-------------------------------------------------------------------------
    static createModel(meshes: pc.Mesh[], meshMaterials?: pc.Material | pc.Material[]): pc.Model {
        let node = new pc.GraphNode();
        let model = new pc.Model();

        model.graph = node;

        if (!meshMaterials)
            meshMaterials = new pc.Material();

        meshes.forEach((mesh: pc.Mesh, i: number) => {
            model.meshInstances.push(new pc.MeshInstance(mesh, Array.isArray(meshMaterials) ? meshMaterials[i] : meshMaterials, node));
        });

        return model;
    }

    //-------------------------------------------------------------------------
    /**
     * Generates normal information from the specified positions and triangle indices.
     * @param positions 
     * @param indices 
     * @param vertexCnt 
     * @returns 
     */
    //-------------------------------------------------------------------------
    static calculateNormals(positions: number[] | Float32Array, indices: number[] | Uint32Array | Uint16Array, vertexCnt: number = positions.length / 3) {
        let triangleCnt = vertexCnt / 2;
        let i1, i2, i3: number;
        let i: number; // Loop counter
        let p1 = new pc.Vec3();
        let p2 = new pc.Vec3();
        let p3 = new pc.Vec3();
        let p1p2 = new pc.Vec3();
        let p1p3 = new pc.Vec3();
        let faceNormal = new pc.Vec3();
        let normals: number[] = [];

        // Initialize the normal array to zero
        for (i = 0; i < positions.length; i++) {
            normals[i] = 0;
        }

        // Accumulate face normals for each vertex
        for (i = 0; i < triangleCnt; i++) {
            i1 = indices[i * 3];
            i2 = indices[i * 3 + 1];
            i3 = indices[i * 3 + 2];

            p1.set(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
            p2.set(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);
            p3.set(positions[i3 * 3], positions[i3 * 3 + 1], positions[i3 * 3 + 2]);

            p1p2.sub2(p2, p1);
            p1p3.sub2(p3, p1);
            faceNormal.cross(p1p2, p1p3).normalize();

            normals[i1 * 3] += faceNormal.x;
            normals[i1 * 3 + 1] += faceNormal.y;
            normals[i1 * 3 + 2] += faceNormal.z;
            normals[i2 * 3] += faceNormal.x;
            normals[i2 * 3 + 1] += faceNormal.y;
            normals[i2 * 3 + 2] += faceNormal.z;
            normals[i3 * 3] += faceNormal.x;
            normals[i3 * 3 + 1] += faceNormal.y;
            normals[i3 * 3 + 2] += faceNormal.z;
        }

        // Normalize all normals
        for (i = 0; i < vertexCnt; i++) {
            let nx = normals[i * 3];
            let ny = normals[i * 3 + 1];
            let nz = normals[i * 3 + 2];
            let invLen = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz);
            normals[i * 3] *= invLen;
            normals[i * 3 + 1] *= invLen;
            normals[i * 3 + 2] *= invLen;
        }

        return normals;
    }

}